<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import { isWhatsAppComplete } from '@chatwoot/utils';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import { useTemplateVariables } from './useTemplateVariables';
import {
  useApprovedTemplates,
  templateFromValue,
  templateToValue,
} from './useApprovedTemplates';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const { templateOptions, templateEntry, templateInboxId } =
  useApprovedTemplates();
const { variableOptions, defaultValues, previewValues } = useTemplateVariables({
  appointment: false,
});

const AFTER_OPTIONS = [10, 15, 30, 45, 60, 120, 240];
const CLOSE_OPTIONS = [30, 60, 120, 240, 480, 1440];

const initialState = {
  inactivityEnabled: true,
  afterMinutes: 30,
  maxNudges: 1,
  closeAfter: 120,
  reengagementEnabled: false,
  template: '',
  params: {},
};

const state = reactive({ ...initialState, params: {} });
const showErrors = ref(false);

// The one "paid templates" switch of the assistant lives in its own page.
const paidEnabled = computed(
  () => props.assistant?.config?.allow_paid_templates === true
);

const minutesLabel = minutes =>
  minutes % 60 === 0
    ? t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.HOURS', { count: minutes / 60 })
    : t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.MINUTES', { count: minutes });

const withCurrent = (options, current) =>
  options.includes(current)
    ? options
    : [...options, current].sort((a, b) => a - b);

const afterOptions = computed(() =>
  withCurrent(AFTER_OPTIONS, state.afterMinutes).map(value => ({
    value,
    label: minutesLabel(value),
  }))
);

const closeOptions = computed(() =>
  withCurrent(CLOSE_OPTIONS, state.closeAfter).map(value => ({
    value,
    label: minutesLabel(value),
  }))
);

const nudgeOptions = computed(() => [
  { value: 0, label: t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.NUDGES_NONE') },
  { value: 1, label: t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.NUDGES_ONE') },
  { value: 2, label: t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.NUDGES_TWO') },
]);

const updateStateFromAssistant = assistant => {
  const settings = assistant?.config?.followup || {};
  Object.assign(state, {
    inactivityEnabled: settings.inactivity_enabled !== false,
    afterMinutes:
      settings.inactivity_after_minutes ?? initialState.afterMinutes,
    maxNudges: settings.max_nudges ?? initialState.maxNudges,
    closeAfter: settings.close_after_minutes ?? initialState.closeAfter,
    reengagementEnabled: settings.reengagement_enabled === true,
    template: templateToValue(settings.reengagement_template),
    params: settings.reengagement_template?.processed_params || {},
  });
};

// A template with a variable left empty cannot be saved.
const templateComplete = computed(
  () =>
    !state.reengagementEnabled ||
    !state.template ||
    !templateEntry(state.template) ||
    isWhatsAppComplete(templateEntry(state.template), state.params)
);

const selectTemplate = value => {
  state.template = value;
  state.params = {};
};

const handleSubmit = () => {
  showErrors.value = true;
  if (!templateComplete.value) return;

  emit('submit', {
    config: {
      ...props.assistant.config,
      followup: {
        inactivity_enabled: state.inactivityEnabled,
        inactivity_after_minutes: Number(state.afterMinutes),
        max_nudges: Number(state.maxNudges),
        close_after_minutes: Number(state.closeAfter),
        reengagement_enabled: state.reengagementEnabled,
        reengagement_template: templateFromValue(state.template, state.params),
      },
    },
  });
};

watch(
  () => props.assistant,
  assistant => {
    if (assistant) updateStateFromAssistant(assistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <SettingsToggleSection
      v-model="state.inactivityEnabled"
      :header="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TOGGLE')"
      :description="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TOGGLE_HELP')"
    >
      <div
        v-if="state.inactivityEnabled"
        data-testid="followup-fields"
        class="grid w-full gap-4 border-t border-n-weak px-4 pb-2 pt-4 sm:grid-cols-3"
      >
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.AFTER') }}
          </label>
          <ComboBox
            v-model.number="state.afterMinutes"
            data-testid="followup-after"
            teleport
            :allow-deselect="false"
            :options="afterOptions"
            :aria-label="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.AFTER')"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.NUDGES') }}
          </label>
          <ComboBox
            v-model.number="state.maxNudges"
            data-testid="followup-nudges"
            teleport
            :allow-deselect="false"
            :options="nudgeOptions"
            :aria-label="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.NUDGES')"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.CLOSE') }}
          </label>
          <ComboBox
            v-model.number="state.closeAfter"
            data-testid="followup-close"
            teleport
            :allow-deselect="false"
            :options="closeOptions"
            :aria-label="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.CLOSE')"
          />
        </div>
        <p class="mb-0 text-xs text-n-slate-11 sm:col-span-3">
          {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.HINT') }}
        </p>
      </div>
    </SettingsToggleSection>

    <div
      data-testid="followup-reengagement"
      class="flex flex-col gap-3 rounded-xl border border-n-weak p-4"
    >
      <label
        class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
      >
        <Checkbox
          data-testid="followup-reengagement-enabled"
          :model-value="state.reengagementEnabled"
          @update:model-value="state.reengagementEnabled = $event"
        />
        {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.REENGAGEMENT') }}
      </label>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.REENGAGEMENT_HELP') }}
      </p>
      <template v-if="state.reengagementEnabled">
        <p
          v-if="!paidEnabled"
          data-testid="followup-paid-hint"
          class="mb-0 text-xs text-n-slate-11"
        >
          {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.PAID_HINT') }}
        </p>
        <div v-else class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TEMPLATE') }}
          </label>
          <ComboBox
            :model-value="state.template"
            data-testid="followup-template"
            teleport
            :allow-deselect="false"
            :options="templateOptions(state.template)"
            :aria-label="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TEMPLATE')"
            @update:model-value="selectTemplate"
          />
          <WhatsAppTemplateParser
            v-if="state.template && templateEntry(state.template)"
            :key="state.template"
            variable-surface="captain"
            :template="templateEntry(state.template)"
            :media-inbox-id="templateInboxId(state.template)"
            :model-value="state.params"
            :variable-options="variableOptions"
            :default-values="defaultValues"
            :preview-values="previewValues"
            @update:model-value="state.params = $event"
          />
          <p
            v-if="showErrors && !templateComplete"
            data-testid="followup-template-error"
            class="mb-0 text-xs text-n-ruby-9"
          >
            {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPLATE_VARIABLES.ERROR') }}
          </p>
        </div>
      </template>
    </div>

    <div>
      <Button
        data-testid="followup-save"
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
